import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { useQcChecklistsQuery } from '../../qc/useQc.js';

import { HOME_CARD_LINK, HOME_CARD_ROW, HomeCard, homeCardStatus } from './HomeCard.js';

/** Five checklists before the card says how many more are waiting. */
const MAX_ROWS = 5;

/**
 * "Quality control to review" — the checklists a crew member has submitted and nobody has signed
 * off yet.
 *
 * `?status=submitted` is the api's own filter, so the narrowing happens in the query rather than
 * over a full list in the browser.
 *
 * `outstandingCount` is what makes a row worth opening first: it counts the fail and needs-review
 * answers that are not waived, and those are what block sign-off. It is stated in words as well as
 * in colour, because colour alone is not a message (A11Y-01).
 */
export function DispatcherQc() {
  const { t } = useTranslation();
  const query = useQcChecklistsQuery({ status: 'submitted' });

  const checklists = query.data?.checklists ?? [];
  const shown = checklists.slice(0, MAX_ROWS);

  return (
    <HomeCard
      headingId="dispatcher-qc-heading"
      title={t('home:dispatcher.qc.heading')}
      action={
        <Link to="/qc" className={HOME_CARD_LINK}>
          {t('home:dispatcher.qc.openAll')}
        </Link>
      }
      status={homeCardStatus(query, shown.length)}
      errorText={t('home:dispatcher.qc.unavailable')}
      emptyText={t('home:dispatcher.qc.empty')}
      onRetry={() => void query.refetch()}
    >
      <ul className="mt-3 flex flex-col gap-2">
        {shown.map((checklist) => (
          <li key={checklist.id}>
            <Link
              to="/qc/$id"
              params={{ id: String(checklist.id) }}
              className={`flex min-h-11 flex-col justify-center hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${HOME_CARD_ROW}`}
            >
              <span className="font-medium text-foreground text-sm">{checklist.name}</span>
              <span className="text-muted-foreground text-xs">
                {checklist.jobName ? `${checklist.jobName} · ` : ''}
                {t('home:dispatcher.qc.answered', {
                  answered: checklist.answeredCount,
                  total: checklist.itemCount,
                })}
                {checklist.outstandingCount > 0 ? (
                  <span className="ml-1 font-medium text-destructive">
                    {t('home:dispatcher.qc.outstanding', { count: checklist.outstandingCount })}
                  </span>
                ) : null}
              </span>
            </Link>
          </li>
        ))}
        {checklists.length > shown.length ? (
          <li className="text-muted-foreground text-xs">
            {t('home:dispatcher.qc.more', { count: checklists.length - shown.length })}
          </li>
        ) : null}
      </ul>
    </HomeCard>
  );
}
